import { useEffect, useRef, useState } from "react";
import { TaarWebRTC, CallMeta } from "@taarai/webrtc-client";
import axios from "axios";
import "./TaarWebRTCUI.css";
import { AutoDialTarget } from "@taarai/webrtc-client/dist/types/types";

export interface AgentCredentials {
  agentCredentials: string;
}

export const Main = () => {
  const [registeredNumber, setRegisteredNumber] = useState("");
  const [isRegistered, setIsRegistered] = useState<boolean>();
  const [dialNumber, setDialNumber] = useState("");
  const [inCallWith, setInCallWith] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [nonce, setNonce] = useState<string>();
  const [status, setStatus] = useState("Disconnected");
  const [dtmfSequence, setDtmfSequence] = useState("");
  const [incomingCall, setIncomingCall] = useState<CallMeta | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const client = useRef<TaarWebRTC | null>(null);

  const prefix = localStorage.getItem("taarprefix") ?? "localhost:9000";

  useEffect(() => {
    client.current = new TaarWebRTC({
      debug: true,
      prefix: prefix,
      onReady: (nonce) => {
        setNonce(nonce);
        setStatus("Ready");
      },
      onCallStarted: (meta: CallMeta) => {
        console.log("On Call Started", meta.id)
        setInCallWith(meta.id);
      },
      onCallEnded: () => {
        setInCallWith(null);
        setIsMuted(false);
        setIncomingCall(null);
      },
      onIncomingCall: (meta: CallMeta) => {
        console.log("Incoming call from:", meta.id);
        setIncomingCall(meta);
        setStatus("Incoming Call");
      },
      onError: (err) => {
        console.error(err);
        setStatus("Error");
      },
    });

    client.current.onMessage('call.completed', (data) => {
      console.log("Call Completed1 Params: ", data)
    });

    client.current.onMessage('register.notice', (data) => {
      console.log("Register Notice: ", data)
    })

  }, []);
  

  const handleRegister = async () => {
    if (!nonce || !registeredNumber) return;
    const res = await axios.post(
      `${prefix}/agent/ext/register`,
      { agentId: registeredNumber, nonce },
      {
        headers: {
          Authorization: "c3f9a8d2e4a74b68917a7b10dfe6b94fba28c5de7c2c4f47a89e3b9c2fd8c5ea",
          "Content-Type": "application/json",
        },
      }
    );
    const creds = res.data as AgentCredentials;
    if (!audioRef.current) throw new Error("Missing audioRef");
    client.current?.register(creds.agentCredentials, audioRef.current);
    setIsRegistered(true);
  };

  const handleCall = () => {
    if (dialNumber) {
      client.current?.makeCall(dialNumber);
    }
  };

  const handleAutoDial = () => {
    if (dialNumber) {
      const targetNumbers = dialNumber.split("|")
      const targets: AutoDialTarget[] = []
      targetNumbers.map((r) => {
        targets.push({
          "metaId": "blah123",
          "callee": r,
          "caller": ["08068130201", "08068130202"]
        });
      })
      const request = {
        targets,
      }
      console.log("Requests~~~~", request)
      client.current?.autoDial(request);
    }
  };

  const handleEndCall = () => {
    client.current?.endCall();
  };

  const toggleMute = () => {
    const shouldMute = !isMuted;
    setIsMuted(shouldMute);
    shouldMute ? client.current?.mute() : client.current?.unmute();
  };

  const setPrefixOverride = (prefix: string) => {
    localStorage.setItem('taarprefix', prefix)
  }

  const sendDTMF = async (tone: string) => {
    if (!client.current || !inCallWith) return;
    
    try {
      // Use the library's built-in sendDTMF method
      await client.current.sendDTMF(tone, 160, 160);
      setDtmfSequence(prev => prev + tone);
      
      console.log(`✅ DTMF tone sent: ${tone}`);
      console.log(`📞 Current sequence: ${dtmfSequence + tone}`);
    } catch (error) {
      console.error("Error sending DTMF:", error);
    }
  };

  const clearDTMFSequence = () => {
    setDtmfSequence("");
  };

  const acceptCall = () => {
    if (incomingCall && client.current) {
      client.current.answerCall();
      setIncomingCall(null);
      setStatus("Call Connected");
    }
  };

  const declineCall = () => {
    if (incomingCall && client.current) {
      // Use the call object's reject method
      (incomingCall as any).reject();
      setIncomingCall(null);
      setStatus("Ready");
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Taar.AI WebRTC Agent</h2>
        <div className="status">● {status}</div>

        <div className="section">
          <label>Override Prefix</label>
          <div className="input-row">
            <input
              type="text"
              onChange={(e) => setPrefixOverride(e.target.value)}
              placeholder={prefix}
            />
          </div>
        </div>

        <div className="section">
          <label>Agent ID to Register</label>
          <div className="input-row">
            <input
              type="text"
              value={registeredNumber}
              onChange={(e) => setRegisteredNumber(e.target.value)}
              placeholder="agent-90312"
            />
            <button
              onClick={handleRegister}
              disabled={!nonce || isRegistered}
            >
              {isRegistered ? 'Registered' : 'Register Agent'}
            </button>
          </div>
        </div>

        <div className="section">
          <label>Number to Dial</label>
          <div className="input-row">
            <input
              type="text"
              value={dialNumber}
              onChange={(e) => setDialNumber(e.target.value)}
              placeholder="+911234567890"
            />
            <button
              onClick={handleCall}
              disabled={!dialNumber || inCallWith !== null}
            >
              Call
            </button>
            <button
              onClick={handleAutoDial}
              disabled={!dialNumber || inCallWith !== null}
            >
              AutoDial
            </button>
          </div>
        </div>

        {incomingCall && (
          <div className="incoming-call-panel">
            <div className="incoming-call-info">
              <h3>📞 Incoming Call</h3>
              <p>From: {incomingCall.id}</p>
            </div>
            <div className="incoming-call-buttons">
              <button onClick={acceptCall} className="accept-call">
                ✅ Accept
              </button>
              <button onClick={declineCall} className="decline-call">
                ❌ Decline
              </button>
            </div>
          </div>
        )}

        {inCallWith && (
          <div className="call-panel">
            <div>In call with {inCallWith}</div>
            <div className="button-row">
              <button onClick={handleEndCall}>End Call</button>
              <button onClick={toggleMute}>{isMuted ? "Unmute" : "Mute"}</button>
            </div>
            
            {/* DTMF Dial Pad */}
            <div className="dtmf-panel">
              <div className="dtmf-display">
                <input 
                  type="text" 
                  value={dtmfSequence} 
                  readOnly 
                  placeholder="DTMF Sequence"
                  className="dtmf-input"
                />
                <button onClick={clearDTMFSequence} className="dtmf-clear">Clear</button>
              </div>
              
              <div className="dtmf-keypad">
                <div className="dtmf-row">
                  <button className="dtmf-key" onClick={() => sendDTMF('1')}>1</button>
                  <button className="dtmf-key" onClick={() => sendDTMF('2')}>2<br/><small>ABC</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('3')}>3<br/><small>DEF</small></button>
                </div>
                <div className="dtmf-row">
                  <button className="dtmf-key" onClick={() => sendDTMF('4')}>4<br/><small>GHI</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('5')}>5<br/><small>JKL</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('6')}>6<br/><small>MNO</small></button>
                </div>
                <div className="dtmf-row">
                  <button className="dtmf-key" onClick={() => sendDTMF('7')}>7<br/><small>PQRS</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('8')}>8<br/><small>TUV</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('9')}>9<br/><small>WXYZ</small></button>
                </div>
                <div className="dtmf-row">
                  <button className="dtmf-key" onClick={() => sendDTMF('*')}>*</button>
                  <button className="dtmf-key" onClick={() => sendDTMF('0')}>0<br/><small>+</small></button>
                  <button className="dtmf-key" onClick={() => sendDTMF('#')}>#</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <audio autoPlay ref={audioRef} className="audio" />

        <div className="footer">
          Powered by <code>@taarai/webrtc-client</code>
        </div>
      </div>
    </div>
  );
}
